import { DiscordSDK, Events } from '@discord/embedded-app-sdk';

const CLIENT_ID = process.env.PUBLIC_DISCORD_CLIENT_ID;

import * as ariseDiscord from './arise-discord-api';
import { useEffect, useRef, useState } from 'react';
import { IS_DISCORD_ACTIVITY } from '@/globals';
import * as arise from '@/arise/api';
import { useAppDispatch, useAppSelector } from '@/state/store';
import { setQuestStatus, setWindowSize } from '@/state/features/discord';
import { updateIsLoggedIn } from '@/state/features/auth';
import { emptyProfile, Profile } from '@/models/profile';
import { setMinimalProfile } from '@/state/features/profile';
import { setShouldHideUI } from '@/state/features/app';
import { getTokenPayload } from '@/utils/auth';
import { setUserId } from '@/utils/analytics/internal';
import { sendEventV2, sendEventV2Once } from '@/utils/analytics';
import { useTheme } from '@/theme';
import { useLang } from '@/components/TranslatedText';

const url = new URL(window.location.href);
const ARISE_SESSION_ID = url.searchParams.get('arise_session_id');
const ARISE_SESSION_DATA = url.searchParams.get('arise_session_data');
const activityStartTime = Date.now();

// remove the session parameters from the URL and update the URL without reloading the page
if (ARISE_SESSION_ID) {
    console.log('[Arise] Removing session ID from URL');
    url.searchParams.delete('arise_session_id');
}
if (ARISE_SESSION_DATA) {
    console.log('[Arise] Removing session data from URL');
    url.searchParams.delete('arise_session_data');
}
window.history.replaceState({}, '', url.toString());

let userDiscordAccessToken: string | null = null;
let discordSDK: DiscordSDK | null = null;

try {
    // We need to instatiate this here on pageload so that the arise frontend does not change the query params in the URL that the discord sdk uses
    discordSDK = new DiscordSDK(CLIENT_ID);
} catch (error) {
    if (IS_DISCORD_ACTIVITY) {
        console.error('[Arise] Discord SDK not instatiated', error);
    }
}
let firstLayoutUpdateReceived = false;

interface DiscordUser {
    username: string;
    discriminator: string;
    id: string;
    public_flags: number;
    avatar?: string | null | undefined;
    global_name?: string | null | undefined;
}

interface AuthData {
    access_token: string;
}

export default function useDiscord() {
    const discord = useRef<DiscordSDK | null>();
    const [user, setUser] = useState<DiscordUser | null>(null);
    const [authLoading, setAuthLoading] = useState(false);
    const [authError, setAuthError] = useState('');
    const [authDenied, setAuthDenied] = useState(false);
    const theme = useTheme();

    const dispatch = useAppDispatch();

    // not sure if we need this
    const [authData, setAuthData] = useState<AuthData | null>();

    const ariseRegister = useAriseRegister();
    const quest = useDiscordQuest();
    const { setLang } = useLang();

    useEffect(() => {
        (async () => {
            try {
                if (!CLIENT_ID) {
                    console.error('[Arise] No PUBLIC_DISCORD_CLIENT_ID set');
                    return;
                }

                discord.current = discordSDK;

                if (!ARISE_SESSION_ID) {
                    console.log('[Arise] Waiting for Discord SDK to be ready');
                    await discord.current.ready();
                    console.log('[Arise] Discord SDK is ready');
                    // authorise immediately
                    await authorize();
                } else {
                    console.log('[Arise] Forcing Discord SDK to be ready');
                    discord.current['isReady'] = true;
                    await discord.current.ready();
                    console.log('[Arise] Discord SDK is ready (forced)');
                    authoriseFromSession();
                }
                sendEventV2Once('discord_authenticated');
                quest.check();
                console.log('[Arise] Discord authorized');
            } catch (error) {
                console.log('[Arise] Could not get discord sdk ready', error);
            }
        })();
    }, []);

    async function setLanguageFromDiscordLocale() {
        try {
            const { locale } = await discord.current.commands.userSettingsGetLocale();
            setLang(locale);
            console.info('[Arise] Setting language to:', locale);
            sendEventV2Once('discord_locale', [locale]);
        } catch (error) {
            console.warn('[Arise] Could not set discord locale:', error);
        }
    }

    async function authoriseFromSession() {
        console.log('[Arise] Authorizing from session');

        if (!ARISE_SESSION_DATA) {
            console.error('[Arise] No session data found in URL');
            return;
        }
        const data = JSON.parse(ARISE_SESSION_DATA);
        console.log('[Arise] Session data from URL', data);
        if (ARISE_SESSION_DATA) {
            if (data.auth && data.authData) {
                setUser(data.auth.user);
                setLanguageFromDiscordLocale();
                setAriseProfile({
                    displayName: data.auth.user.global_name || data.auth.user.username,
                });
                userDiscordAccessToken = data.authData.access_token;
                ariseDiscord.setAuthToken(data.authData.token);
                setAuthData(data.authData);
                console.log('[Arise] Restored Discord session from localStorage');
            } else {
                console.error('[Arise] invalid session data in localStorage', ARISE_SESSION_DATA);
            }
        } else {
            console.error('[Arise] No session data found in localStorage');
        }
    }

    async function authorize() {
        if (authLoading) {
            return;
        }
        setAuthLoading(true);
        setAuthDenied(false);
        setAuthError('');
        try {
            console.log('[Arise] No auth code found in URL, requesting authorization from Discord');
            const authRes = await discord.current.commands.authorize({
                client_id: CLIENT_ID,
                response_type: 'code',
                state: '',
                prompt: 'none',
                scope: ['identify', 'applications.entitlements'],
            });
            const code = authRes.code;

            sendEventV2('discordlogin__prompt_accepted');
            let authData: any;
            try {
                authData = await tryXTimes(async () => {
                    const res = await ariseDiscord.authorize(code);
                    if (!res.access_token) {
                        throw new Error('No auth data');
                    }
                    return res;
                }, 3);
            } catch (error) {
                console.error('[Arise] Error exchanging auth code for access token', error);
                sendEventV2('discordlogin__authexchange_failed');
                const fallbackDisplayName = theme?.customJSON?.settings?.guestUsernamePrefix ?? 'Guest';
                setAriseProfile({
                    displayName: fallbackDisplayName,
                });
                // @ts-ignore
                setUser({ username: fallbackDisplayName });
                setAuthLoading(false);
                return;
            }

            const auth = await discord.current.commands.authenticate({
                access_token: authData.access_token,
            });
            // Setup listeners
            // note: layout listerners do not work on password protected activities
            setupLayoutListeners();
            setUser(auth.user);
            setAriseProfile({
                displayName: auth.user.global_name || auth.user.username,
            });
            setLanguageFromDiscordLocale();
            userDiscordAccessToken = authData.access_token;
            setAuthLoading(false);
            setAuthData(authData);
            ariseDiscord.setAuthToken(authData.token);
        } catch (error) {
            // User denied authorization
            if (error.code === 5000) {
                sendEventV2('discordlogin__prompt_denied');
                setAuthDenied(true);
            } else if (error.code === 4002) {
                // Already authing, ignore
            } else {
                sendEventV2('discordlogin__error', [
                    error instanceof Error ? error.message : typeof error === 'string' ? error : JSON.stringify(error),
                ]);
                console.log('[Arise] Authorizing Error', error);
                setAuthError('Error authorizing with Discord');
            }
        } finally {
            setAuthLoading(false);
        }

        // Register with Arise
        // This is an async call that doesn't return anything so we don't need to await it
        ariseRegisterFlow();
    }

    function setAriseProfile({ displayName }) {
        const profile = emptyProfile();
        profile.displayName = displayName;
        dispatch(setMinimalProfile(profile));
    }

    async function ariseRegisterFlow() {
        try {
            await ariseRegister.register();
        } catch (e) {
            console.error('[Arise] Error registering with Arise', e);
        }
    }

    function openExternalLink(url: string) {
        if (discord.current) {
            discord.current.commands.openExternalLink({
                url,
            });
        }
    }

    function onMinimise() {
        console.log('[Arise] Minimise');
        dispatch(setWindowSize('minimized'));
        dispatch(setShouldHideUI(true));
    }

    function onMaximise() {
        console.log('[Arise] Maximise');
        dispatch(setWindowSize('maximized'));
        dispatch(setShouldHideUI(false));

        setTimeout(() => {
            quest.recheckEnrollment();
        }, 1000);
    }

    async function setupLayoutListeners() {
        const onActivityLayoutModeUpdate = (e: any) => {
            console.log('[Arise] Activity layout mode update', e);
            if (!firstLayoutUpdateReceived) {
                firstLayoutUpdateReceived = true;
                return;
            }
            if (e.layout_mode === 1) {
                onMinimise();
            } else if (e.layout_mode === 0) {
                onMaximise();
            } else {
                console.log('[Arise] Unhandled layout mode', e.layout_mode);
                onMaximise();
            }
        };

        async function onQuestStatusUpdate(e: any) {
            console.log('[Arise] Quest status update from sdk', e);
        }

        if (discord.current) {
            await discord.current.subscribe(Events.ACTIVITY_LAYOUT_MODE_UPDATE, onActivityLayoutModeUpdate);
            await discord.current.subscribe(Events.QUEST_ENROLLMENT_STATUS_UPDATE, onQuestStatusUpdate);
        } else {
            console.error('[Arise] Unable to setup listener');
        }

        return () => {
            if (discord.current) {
                discord.current.unsubscribe(Events.ACTIVITY_LAYOUT_MODE_UPDATE, onActivityLayoutModeUpdate);
                discord.current.unsubscribe(Events.QUEST_ENROLLMENT_STATUS_UPDATE, onQuestStatusUpdate);
            }
        };
    }

    return {
        authorize,
        openExternalLink,
        user,
        authError,
        ariseRegister,
        authDenied,
        quest,
        authLoading,
    };
}

function useAriseRegister() {
    const dispatch = useAppDispatch();
    const [trying, setTrying] = useState(false);
    const [failed, setFailed] = useState(false);

    async function register() {
        const totalAttempts = 3;
        let attempts = 0;
        let success = false;
        setTrying(true);
        while (!success && attempts < totalAttempts) {
            try {
                const res = await ariseDiscord.register();
                arise.setAuthToken(res.authToken);
                dispatch(updateIsLoggedIn(true));
                try {
                    const payload = getTokenPayload(res.authToken);
                    setUserId(payload.sub);
                } catch (error) {}
                success = true;
                break;
            } catch (error) {
                console.log('[Arise] Error registering with Arise', error);
                attempts++;
            }
        }
        setTrying(false);
        if (!success) {
            setFailed(true);
        }
    }
    return { register, trying, failed };
}

function useDiscordQuest() {
    const id = process.env.PUBLIC_DISCORD_QUEST_ID;

    const dispatch = useAppDispatch();
    const status = useAppSelector((state) => state.discord.questStatus);
    const checked = useAppSelector((state) => state.discord.questStatusChecked);

    async function check() {
        try {
            getQuestStatusAsync();

            const data = await ariseDiscord.checkQuest();
            let status = 'not_enrolled';
            if (data.completed_at) {
                status = 'completed';
            } else if (data.enrolled_at) {
                status = 'enrolled';
            }
            dispatch(setQuestStatus(status));
            return {
                status,
                checked: true,
            };
        } catch (error) {
            console.log('[Arise] Error checking quest status', error);
        }
        return { status: 'not_enrolled', checked: false };
    }

    async function complete() {
        try {
            await ariseDiscord.completeQuest();
            dispatch(setQuestStatus('completed'));
            sendEventV2('quest_complete', [], [Date.now() - activityStartTime]);
        } catch (error) {
            console.log('[Arise] Error completing quest', error);
        }
    }

    async function recheckEnrollment() {
        console.log('[Arise] Rechecking enrollment');
        if (status === 'not_enrolled' || !status) {
            await check();
        }
    }
    return { check, checked, status, complete, recheckEnrollment, id };
}

export function gotoQuest() {
    if (discordSDK) {
        discordSDK.commands.openExternalLink({
            url: `https://discord.com/quests/${process.env.PUBLIC_DISCORD_QUEST_ID}`,
        });
    }
}

export function openExternalLinkDiscord(url: string) {
    if (discordSDK && url) {
        discordSDK.commands.openExternalLink({
            url,
        });
    }
}

export function isDiscordOnMobile() {
    return discordSDK?.platform === 'mobile';
}

export function discordPlatform() {
    return discordSDK?.platform;
}

export async function triggerDiscordShare(text: string = '') {
    if (discordSDK) {
        try {
            const shouldLinkToQuest = process.env.PUBLIC_DISCORD_QUEST_ID;

            const url = shouldLinkToQuest
                ? `https://discord.com/quests/${process.env.PUBLIC_DISCORD_QUEST_ID}`
                : `https://discord.com/activities/${process.env.PUBLIC_DISCORD_CLIENT_ID}`;

            sendEventV2('share', ['discord_internal', text, url]);

            await discordSDK.commands.shareLink({
                message: `${text} ${url}`,
            });
        } catch (error) {
            console.error('[Arise] Share Dialogue Error: ', JSON.stringify(error));
        }
    } else {
        console.error('[Arise] Discord SDK not available');
    }
}

// async function getShareImageUrl() {
//     function getUrl() {
//         switch (process.env.PUBLIC_INSTANCE) {
//             case 'mimas':
//                 return '/.proxy/cdn/general/share.jpg';
//             case 'deimos':
//                 return '/.proxy/cdn/general/vcp-share.jpg';
//             case 'umbra':
//                 return '/.proxy/twdprod/uploads/fd87c2e6-0572-4b00-b6c7-31f9a3cdc2d3/BP-SocialShare.png';
//             default:
//                 console.error('No share image found for instance:', process.env.PUBLIC_INSTANCE);
//                 return null;
//         }
//     }

//     const imageURL = getUrl();
//     // get image data
//     const response = await fetch(imageURL);
//     const blob = await response.blob();
//     const mimeType = 'image/jpeg';

//     const buf = await blob.arrayBuffer();
//     const imageFile = new File([buf], 'image.jpg', { type: mimeType });
//     const body = new FormData();
//     body.append('file', imageFile);

//     const attachmentResponse = await fetch(`https://discord.com/api/v10/applications/${CLIENT_ID}/attachment`, {
//         method: 'POST',
//         headers: {
//             Authorization: `Bearer ${userDiscordAccessToken}`,
//         },
//         body,
//     });
//     const attachmentJson = await attachmentResponse.json();
//     const mediaUrl = attachmentJson.attachment.url;
//     return mediaUrl;
// }

function tryXTimes(fn: () => Promise<any>, times: number) {
    return new Promise((resolve, reject) => {
        let attempts = 0;
        const tryFn = async () => {
            try {
                const res = await fn();
                resolve(res);
            } catch (error) {
                console.log(`[Arise] Attempt ${attempts + 1} failed`, error);
                if (attempts < times) {
                    attempts++;
                    setTimeout(tryFn, 1000);
                } else {
                    console.error(`[Arise] All ${times} attempts failed`, error);
                    reject(error);
                }
            }
        };
        tryFn();
    });
}

async function getQuestStatusAsync() {
    const id = process.env.PUBLIC_DISCORD_QUEST_ID;
    const status = await discordSDK?.commands.getQuestEnrollmentStatus({
        quest_id: id || '',
    });

    console.log('[Arise] Quest status from sdk', status);
}
